import { z } from "zod";
import { stepTypeKey } from "../../drizzle/stepTypes.js";
import { taskChannel } from "../../drizzle/taskTypes.js";
import { attachmentCategorySchema, documentCategory, documentCategorySchema } from "../../workflows/document-categories.js";
import { createEmailTaskSchema } from "../../workflows/email-tasks.js";
import { glAccountSchema } from "../../workflows/gl-accounts.js";
import { CLAIMINI_ID_ENTITY_NAME, attachmentSchema, invoicePositionEntityNames } from "../../workflows/heidelbot-entities.js";
import { invoicePdrTaskSchema } from "../../workflows/pdr-tasks.js";
import { withStepMetadata } from "./step-metadata.js";
/** Email-channel invoice data: the Heidelbot email contract, attachments carry a `positions` array. */
const invoiceEmailDataSchema = createEmailTaskSchema.omit({ type: true }).extend({
    channel: z.literal(taskChannel.EMAIL),
    attachments: z.array(attachmentSchema).default([]),
});
/** Invoice data for the pdr.cloud channel - the pdr contract's invoice arm. */
const invoicePdrDataSchema = invoicePdrTaskSchema.omit({ type: true }).extend({
    channel: z.literal(taskChannel.PDR),
});
/** Invoice data schema, discriminated on `channel`. */
export const invoiceDataSchema = z.discriminatedUnion("channel", [invoiceEmailDataSchema, invoicePdrDataSchema]);
/** Invoice data as the invoice_check step receives it, one metadata-wrapped arm per channel. */
export const invoiceDataWithMetadataSchema = z.discriminatedUnion("channel", [
    withStepMetadata(invoiceEmailDataSchema),
    withStepMetadata(invoicePdrDataSchema),
]);
/** Header entity names extracted by Heidelbot for the invoice workflow. */
export const invoiceEntityNames = {
    CLAIMINI_ID: CLAIMINI_ID_ENTITY_NAME,
    PERSONAL_ACCOUNT: "personalAccount",
    TOTAL_NET: "totalNet",
    TOTAL_TAX: "totalTax",
    TOTAL_GROSS: "totalGross",
    DOCUMENT_DATE: "documentDate",
    DOCUMENT_NUMBER: "documentNumber",
    SERVICE_PROVISION_FROM: "serviceProvisionFrom",
    SERVICE_PROVISION_TO: "serviceProvisionTo",
};
export { invoicePositionEntityNames };
/**
 * One captured position (line item) inside an invoice entry. Cents-as-int for monetary fields.
 * `glAccount` is the user-selected booking target from the {@link glAccountSchema} enum.
 */
export const invoicePositionSchema = z.object({
    glAccount: glAccountSchema,
    net: z.int(),
    tax: z.int(),
    gross: z.int(),
});
/**
 * Shape of an invoice branch step's `result`: one fully captured invoice entry.
 * The outcome (which of the 5 branch step types, e.g. booked vs. rejected)
 * is encoded by the step's `type`, not a field.
 */
export const invoiceEntrySchema = z.object({
    attachmentUrl: z.url(),
    personalAccount: z.string().min(1),
    documentNumber: z.string().min(1),
    documentDate: z.iso.date(), // YYYY-MM-DD
    serviceProvisionFrom: z.iso.date().nullable(),
    serviceProvisionTo: z.iso.date().nullable(),
    totalNet: z.int(),
    totalTax: z.int(),
    totalGross: z.int(),
    positions: z.array(invoicePositionSchema).min(1),
    // Deviating payment details ("Abweichende Zahlungsangaben")
    deviatingPayee: z.string().nullable(),
    exclusionUntil: z.iso.date().nullable(),
    paymentDue: z.iso.date().nullable(),
});
/** Branch step type keys that an invoice entry can be committed under. */
export const invoiceBranchStepTypeKeys = [
    stepTypeKey.INVOICE_RECORD_AND_BOOK,
    stepTypeKey.INVOICE_RECORD_AND_REASSIGN,
    stepTypeKey.INVOICE_RECORD_AND_COMPLAIN,
    stepTypeKey.INVOICE_RECORD_AND_CREATE_INCIDENT,
    stepTypeKey.INVOICE_REJECT,
];
export const invoiceBranchStepTypeKeySchema = z.enum(invoiceBranchStepTypeKeys);
/** Per-entry branch step titles shown in the step list. */
export const invoiceBranchStepTitleByType = {
    [stepTypeKey.INVOICE_RECORD_AND_BOOK]: "Beleg erfassen + buchen",
    [stepTypeKey.INVOICE_RECORD_AND_REASSIGN]: "Beleg erfassen + Umschreibung anfordern / umschreiben",
    [stepTypeKey.INVOICE_RECORD_AND_COMPLAIN]: "Beleg erfassen + reklamieren",
    [stepTypeKey.INVOICE_RECORD_AND_CREATE_INCIDENT]: "Beleg erfassen + neues Ereignis anlegen",
    [stepTypeKey.INVOICE_REJECT]: "Beleg ablehnen",
};
const SUPPLIER_DOCUMENT_CATEGORIES = new Set([
    documentCategory.INVOICE_FROM_SUPPLIER,
    documentCategory.CREDIT_FROM_SUPPLIER,
]);
/**
 * Categories an invoice entry's attachment may carry, per decision (branch step). Every recording
 * decision needs a supplier invoice or credit note; only a reject keeps the plain "Beleg".
 */
export const INVOICE_ENTRY_ATTACHMENT_CATEGORIES_BY_BRANCH = {
    [stepTypeKey.INVOICE_RECORD_AND_BOOK]: SUPPLIER_DOCUMENT_CATEGORIES,
    [stepTypeKey.INVOICE_RECORD_AND_CREATE_INCIDENT]: SUPPLIER_DOCUMENT_CATEGORIES,
    [stepTypeKey.INVOICE_RECORD_AND_REASSIGN]: SUPPLIER_DOCUMENT_CATEGORIES,
    [stepTypeKey.INVOICE_RECORD_AND_COMPLAIN]: SUPPLIER_DOCUMENT_CATEGORIES,
    [stepTypeKey.INVOICE_REJECT]: new Set([documentCategory.DOCUMENT]),
};
/** Union over all decisions: what the attachment picker and the "Standard" linking area accept. */
export const INVOICE_ENTRY_ATTACHMENT_CATEGORIES = new Set(Object.values(INVOICE_ENTRY_ATTACHMENT_CATEGORIES_BY_BRANCH).flatMap((categories) => [...categories]));
/**
 * Task-level data the reviewer commits when the whole task is finalized: the shared
 * HDO claim (`claiminiId`) and the per-attachment category labels. One per task —
 * shared by every entry. Per-entry data lives in each invoice branch step's `result`.
 */
export const invoiceTaskLevelDataSchema = z.object({
    claiminiId: z.string().min(1),
    attachmentCategories: z.array(attachmentCategorySchema).default([]),
    notes: z.string().optional(),
});
/**
 * Thin cross-reference recorded in the check step's result, linking the task-level
 * review to its sibling branch steps. Each entry references the branch step that
 * persisted that invoice entry and its chosen outcome.
 */
export const invoiceEntryRefSchema = z.object({
    stepId: z.int().positive(),
    outcome: invoiceBranchStepTypeKeySchema,
});
/**
 * The check step's final result — set when the user clicks "Speichern und nächster Task".
 * Wraps the original Heidelbot data, the task-level reviewed data, and the list of
 * branch step references created during the review.
 */
export const invoiceReviewResultSchema = z.object({
    originalData: invoiceDataWithMetadataSchema,
    taskLevelData: invoiceTaskLevelDataSchema,
    entries: z
        .array(invoiceEntryRefSchema)
        .min(1)
        .refine((entries) => new Set(entries.map((e) => e.stepId)).size === entries.length, {
        message: "entries must reference each branch step at most once",
    }),
    reviewedAt: z.iso.datetime(),
});
/**
 * Check step review result + step metadata. Set as the `input` on any follow-up step
 * the workflow exposes that needs the check step snapshot.
 */
export const invoiceReviewResultWithMetadataSchema = withStepMetadata(invoiceReviewResultSchema);
/**
 * `input` metadata persisted on each invoice branch step. Records the chaining pointer
 * plus the source/target claim ids resolved at commit time.
 *
 * `targetClaiminiId` is the task's locked HDO claim: once the first entry is committed,
 * every later entry must reuse it. It is read back to (a) enforce the claiminiId lock on the
 * backend and (b) seed the now-read-only form field after a reload — see the lock in the
 * invoice workflow service.
 */
export const invoiceBranchStepInputSchema = z.object({
    previousStepId: z.int().positive(),
    // The claiminiId the user had in the form at commit time (the request value). May be null
    // for a create-incident first entry where the user had no existing incident to reference.
    sourceClaiminiId: z.string().nullable(),
    // Where the entry was actually written: the source id for book/reassign/complain, the freshly
    // minted id for create-incident, or the task's already-locked id for any subsequent entry.
    targetClaiminiId: z.string().min(1),
    // The category the entry's attachment carried when it was sent to eClaim. Restored on a mid-task
    // reload so the locked category shows what eClaim holds, not the Heidelbot guess. Optional
    // for backward compatibility
    attachmentCategory: documentCategorySchema.optional(),
});
